import { motion } from "framer-motion";
import { Recycle, Leaf, Zap } from "lucide-react";
import { PageHero, SectionHeading, reveal } from "@/components/site/primitives";
import Seo from "@/components/site/Seo";
import { CTABand } from "@/pages/BusinessWaste";
import { CONTACT, IMAGES } from "@/lib/data";
import { useContent } from "@/i18n/content";
import { useI18n } from "@/i18n";

export default function RecyclingServices() {
  const { t } = useI18n();
  const { WASTE_TYPES } = useContent();
  const serviceJsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Commercial Recycling Services in Miami-Dade",
    serviceType: "Commercial recycling and waste diversion",
    provider: { "@id": "https://eewaste.com/#business" },
    areaServed: { "@type": "AdministrativeArea", name: CONTACT.area },
    url: "https://eewaste.com/recycling",
  };
  const PILLARS = [
    { icon: Recycle, t: t("rp.p0t"), d: t("rp.p0d") },
    { icon: Zap, t: t("rp.p1t"), d: t("rp.p1d") },
    { icon: Leaf, t: t("rp.p2t"), d: t("rp.p2d") },
  ];
  return (
    <>
      <Seo
        title={t("rp.seoTitle")}
        description={t("rp.seoDesc")}
        path="/recycling"
        image={IMAGES.recycling}
        jsonLd={serviceJsonLd}
      />
      <PageHero
        crumb={t("rp.crumb")}
        eyebrow={t("rp.eyebrow")}
        title={t("rp.title")}
        highlight={t("rp.highlight")}
        subtitle={t("rp.subtitle")}
      />

      <section className="relative bg-[#FBFAF8] py-24">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 grid lg:grid-cols-[1fr_0.9fr] gap-12 items-center">
          <motion.div variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <SectionHeading eyebrow={t("rp.commitEyebrow")} title={t("rp.commitTitle")} highlight={t("rp.commitHighlight")} />
            <div className="mt-8 space-y-6">
              {PILLARS.map((p) => (
                <div key={p.t} className="flex items-start gap-4">
                  <span className="grid place-items-center w-11 h-11 rounded-xl bg-emerald-600/10 text-emerald-700 shrink-0"><p.icon size={22} /></span>
                  <div>
                    <h3 className="font-display font-bold text-lg text-[#0B0F14]">{p.t}</h3>
                    <p className="text-slate-600 text-sm mt-1">{p.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
          <motion.div variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: true }}
            className="relative rounded-3xl overflow-hidden aspect-square border border-[#E7E5DF] shadow-soft">
            <img loading="lazy" decoding="async" src={IMAGES.recycling} alt="Recycling facility" className="w-full h-full object-cover" />
          </motion.div>
        </div>
      </section>

      <section className="relative bg-[#F0EFEA] py-24">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
          <SectionHeading center eyebrow={t("rp.handleEyebrow")} title={t("rp.handleTitle")} highlight={t("rp.handleHighlight")} className="mb-14" />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-px bg-[#E7E5DF] border border-[#E7E5DF] rounded-2xl overflow-hidden">
            {WASTE_TYPES.map((type, i) => (
              <motion.div key={type} custom={i} variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: true }}
                className="bg-white p-6 min-h-[130px] flex flex-col justify-between hover:bg-[#FBFAF8] transition-colors" data-testid={`recycle-type-${i}`}>
                <span className="font-mono-tech text-xs text-emerald-700/70">{String(i + 1).padStart(2, "0")}</span>
                <span className="font-display font-semibold text-[#0B0F14] text-sm sm:text-base leading-tight">{type}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative bg-[#FBFAF8] py-20">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <SectionHeading eyebrow={t("rp.seoEyebrow")} title={t("rp.seoTitle2")} highlight={t("rp.seoHighlight")} />
          <div className="mt-6 space-y-4 text-slate-600 leading-relaxed" data-testid="recycling-seo-copy">
            <p>{t("rp.copy1")}</p>
            <p>{t("rp.copy2")}</p>
          </div>
        </div>
      </section>

      <CTABand />
    </>
  );
}
